import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { invalidateQuoteRequestLineDetails } from "#modules/optimizations/infra/invalidateQuoteRequestLineDetails";
import {
  getOptimizationsQueryData,
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";
import { withPreservedCompatibleLines } from "#modules/optimizations/infra/preserveCompatibleLines";
import { toggleOptimization } from "#modules/optimizations/infra/toggleOptimization.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";

export const useToggleOptimization = ({ quoteRequestId }: { quoteRequestId: string }) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: toggleOptimization,
    onMutate: async ({ optimizationId, enabled }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousEnabled = getOptimizationsQueryData(queryClient, quoteRequestId)?.find(
        (optimization) => optimization.id === optimizationId,
      )?.enabled;

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId ? { ...optimization, enabled } : optimization,
        ),
      );

      return { previousEnabled };
    },
    onError: (err: Error, { optimizationId }, context) => {
      // Roll back this optimization only, so a failing toggle does not clobber the concurrent ones
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId && context?.previousEnabled !== undefined
            ? { ...optimization, enabled: context.previousEnabled }
            : optimization,
        ),
      );

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSuccess: (updatedOptimization) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === updatedOptimization.id
            ? withPreservedCompatibleLines(optimization, updatedOptimization)
            : optimization,
        ),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.optimizations.enabledByQuoteRequest(quoteRequestId),
      });
      invalidateQuoteRequestLineDetails(
        queryClient,
        updatedOptimization.quoteRequestLines.map((line) => line.id),
      );
    },
  });

  return { toggleOptimization: mutate, toggleOptimizationAsync: mutateAsync, ...rest };
};
